import { useMemo, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { DataTable } from "@/components/ui/DataTable/index";
import { DataTableColumn } from "@/components/ui/DataTable/types";
import { NeedsAttentionRow } from "@/lib/api/risk/brokerage/tt/v2";
import { buildBaseColumns, DEFAULT_COLUMN_VISIBILITY } from "./columns";
import { REASON_BADGE_CLASS, REASON_LABELS } from "./reasonChips";

interface TtOrdersTableProps {
  rows: NeedsAttentionRow[];
  selectedGuid: string | null;
  onOpen: (guidOrderId: string) => void;
  isLoading: boolean;
}

export function TtOrdersTable({
  rows,
  selectedGuid,
  onOpen,
  isLoading,
}: TtOrdersTableProps) {

  const columns: DataTableColumn<NeedsAttentionRow>[] = useMemo(() => {
    const base = buildBaseColumns();

    const actionsCol: DataTableColumn<NeedsAttentionRow> = {
      id: "actions",
      header: "",
      size: 100,
      minSize: 80,
      disableRowClick: true,
      cell: (info) => {
        const row = info.row.original;
        const isSelected = row.guid_order_id === selectedGuid;
        return (
          <Button
            variant={isSelected ? "default" : "outline"}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(row.guid_order_id);
            }}
          >
            {isSelected ? "Selected" : "Open"}
          </Button>
        );
      },
    };

    const reasonsCol: DataTableColumn<NeedsAttentionRow> = {
      id: "reasons",
      header: "Reasons",
      size: 240,
      minSize: 180,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-wrap gap-1">
            {row.needs_attention_reasons.map((reason) => (
              <Badge
                key={reason}
                variant="outline"
                className={cn("text-xs", REASON_BADGE_CLASS)}
              >
                {REASON_LABELS[reason]}
              </Badge>
            ))}
          </div>
        );
      },
    };

    return [actionsCol, ...base, reasonsCol];
  }, [selectedGuid, onOpen]);

  return (
    <div className="flex flex-col gap-3 min-w-0 flex-1">
        <DataTable
          data={rows}
          columns={columns}
          enableSorting
          enableMultiSort
          enableFiltering
          enableMultiFilter
          enableColumnResizing
          enableColumnVisibility
          enableColumnPinning
          enableExport
          defaultCondensed
          defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
          enableVirtualization
          virtualizationRowHeight={65}
          virtualizationOverscan={2}
          defaultSorting={[{ id: "exchange_event_time", desc: true }]}
          emptyMessage={isLoading ? "Loading orders" : "No orders need attention."}
          stickyHeaders
          tableClassName="max-h-[calc(100vh-260px)] overflow-y-scroll"
        />
    </div>
  );
}
